import { action, computed, makeObservable, observable, reaction } from 'mobx';

import type { CleanGeoData, RawGeoData } from '../../features/geography/types';
import { analytics } from '../../services/analytics';
import { getRequest } from '../../services/api';
import { setLastKnownRawGeoData } from '../../services/launchDarkly';
import { getErrorMessage } from '../../utils/errors';
import { encodeGeoCoords, getUserLocation } from '../../utils/getUserLocation';

export class LiveGeoStore {
  constructor() {
    makeObservable(this);

    void this.getCurrentGeoData();

    reaction(
      () => this.rawGeo,
      (rawGeo) => {
        if (rawGeo) {
          analytics.setCommonProps({
            country_code: rawGeo.countryCode,
            ip_country: rawGeo.countryCode,
            ip_region: rawGeo.region,
            ip_city: rawGeo.city,
          });
        }
      },
    );

    reaction(
      () => this.currentGeo,
      (cleanGeo) => {
        if (cleanGeo) {
          analytics.setCommonProps({
            territory_key: cleanGeo.territoryKey,
          });
        }
      },
    );
  }

  public fetchCurrentGeo = async () => {
    try {
      const geoData = await getUserLocation();
      const res = await getRequest<{
        data: {
          clean: CleanGeoData | null;
          raw: RawGeoData | null;
        };
      }>('geography/current-geo', undefined, undefined, {
        headers: { 'x-geo-coords': encodeGeoCoords(geoData) },
      });

      this.setCurrentGeo(res.data.clean, res.data.raw);

      return res.data;
    } catch (geoError) {
      const errorMessage = getErrorMessage(geoError);
      console.error(`Error fetching current geo: ${errorMessage}`);
      return null;
    }
  };

  private currentGeoRequest: ReturnType<typeof this.fetchCurrentGeo> | null = null;

  // One current-geo request per session: the constructor and render.tsx both ask for it at boot and share
  // the result. A failed request is dropped so the next caller can retry.
  public getCurrentGeoData = () => {
    this.currentGeoRequest ??= this.fetchCurrentGeo().then((data) => {
      if (!data) {
        this.currentGeoRequest = null;
      }
      return data;
    });
    return this.currentGeoRequest;
  };

  // Drops the session cache and fetches again — the debug console's Refresh, after a geo override change.
  public refreshCurrentGeo = () => {
    this.currentGeoRequest = null;
    return this.getCurrentGeoData();
  };

  @observable
  public currentGeo: CleanGeoData | null = null;

  @observable
  public rawGeo: RawGeoData | null = null;

  @computed
  public get countryCode() {
    return this.currentGeo?.countryCode ?? this.rawGeo?.countryCode;
  }

  @computed
  public get region() {
    return this.currentGeo?.territoryName ?? this.rawGeo?.region;
  }

  @computed
  public get isLocationInEurope() {
    return Boolean(this.countryCode && ['DE', 'AT'].includes(this.countryCode));
  }

  @action
  private setCurrentGeo(geo: CleanGeoData | null, raw: RawGeoData | null) {
    this.currentGeo = geo;
    this.rawGeo = raw;
    // Lets createLDContext keep `ip_country` on re-identifies that only carry a profile.
    setLastKnownRawGeoData(raw);
  }
}

export const liveGeoStore = new LiveGeoStore();
